(() => {
  const SELECTORS = {
    cards: '[data-locator-cards]',
    card: '[data-location-card]',
    cardTitle: '.store-location-card__title',
    searchForm: '[data-locator-search-form]',
    search: '[data-locator-search]',
    searchSubmit: '[data-locator-search-submit]',
    filter: '[data-locator-filter]',
    status: '[data-locator-status]',
    results: '[data-locator-results]',
    empty: '[data-locator-empty]',
    reset: '[data-locator-reset]',
    geolocate: '[data-locator-geolocate]',
    geolocateLabel: '[data-locator-geolocate-label]',
    geolocationStatus: '[data-locator-geolocation-status]',
    routeControls: '[data-locator-route-controls]',
    routeMode: '[data-locator-route-mode]',
    routeStatus: '[data-locator-route-status]',
    select: '[data-location-select]',
    result: '[data-location-result]',
    mapLayout: '.store-locator__map-layout',
    map: '[data-locator-map]',
    mapLegend: '[data-locator-map-legend]',
    mapFallback: '[data-locator-map-fallback]',
    schedule: '[data-location-schedule]',
    hoursWidget: '[data-opening-hours-widget]',
  };

  class StoreLocator extends HTMLElement {
    static normalizeText(value = '') {
      return String(value)
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    }

    static matches(location, query, typeFilter) {
      const matchesQuery = query === '' || location.searchText.includes(query);
      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'stores' && ['store', 'store-workshop'].includes(location.type)) ||
        (typeFilter === 'workshops' && ['workshop', 'store-workshop'].includes(location.type));

      return matchesQuery && matchesType;
    }

    static parseCoordinate(value, minimum, maximum) {
      const coordinate = Number.parseFloat(value);
      return Number.isFinite(coordinate) && coordinate >= minimum && coordinate <= maximum
        ? coordinate
        : null;
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.hoursApi = window.StoreOpeningHours || null;
      this.distanceApi = window.StoreLocationDistance || null;
      this.routeApi = window.StoreRoutePreview || null;
      this.geocoderApi = window.StoreLocationGeocoder || null;
      this.elements = {
        cards: this.querySelector(SELECTORS.cards),
        searchForm: this.querySelector(SELECTORS.searchForm),
        search: this.querySelector(SELECTORS.search),
        searchSubmit: this.querySelector(SELECTORS.searchSubmit),
        filters: [...this.querySelectorAll(SELECTORS.filter)],
        status: this.querySelector(SELECTORS.status),
        results: this.querySelector(SELECTORS.results),
        empty: this.querySelector(SELECTORS.empty),
        reset: this.querySelector(SELECTORS.reset),
        geolocate: this.querySelector(SELECTORS.geolocate),
        geolocateLabel: this.querySelector(SELECTORS.geolocateLabel),
        geolocationStatus: this.querySelector(SELECTORS.geolocationStatus),
        routeControls: this.querySelector(SELECTORS.routeControls),
        routeModes: [...this.querySelectorAll(SELECTORS.routeMode)],
        routeStatus: this.querySelector(SELECTORS.routeStatus),
        mapLayout: this.querySelector(SELECTORS.mapLayout),
        map: this.querySelector(SELECTORS.map),
        mapLegend: this.querySelector(SELECTORS.mapLegend),
        mapFallback: this.querySelector(SELECTORS.mapFallback),
      };

      if (!this.elements.cards || !this.elements.search || !this.elements.results) return;

      this.locations = [...this.elements.cards.querySelectorAll(SELECTORS.card)]
        .filter((card) => card.dataset.locationActive === 'true')
        .map((card) => this.parseLocation(card));
      this.locationsById = new Map(this.locations.map((location) => [location.id, location]));
      this.enhanceLocationCards();

      this.state = {
        query: '',
        searchMode: 'local',
        typeFilter: 'all',
        selectedLocationId: null,
        nearestLocationId: null,
        visibleLocationIds: new Set(),
        userLocation: null,
        routeMode: 'driving',
        routeDestinationId: null,
        mapView: ['monochrome', 'street', 'satellite'].includes(this.dataset.mapView)
          ? this.dataset.mapView
          : 'monochrome',
      };
      this.placeCache = new Map();
      this.routeCache = new Map();

      this.bindEvents();
      this.initMap();
      this.elements.results.hidden = false;
      this.dataset.enhanced = 'true';
      this.update();
      this.startOpeningHoursRefresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.placeAbortController?.abort();
      this.routeAbortController?.abort();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.cardTitleAlignmentFrame);
      window.clearTimeout(this.routeRequestTimer);
      window.clearInterval(this.hoursRefreshTimer);
      this.destroyMap();
      this.initialized = false;
    }

    parseLocation(card) {
      const latitude = StoreLocator.parseCoordinate(card.dataset.locationLatitude, -90, 90);
      const longitude = StoreLocator.parseCoordinate(card.dataset.locationLongitude, -180, 180);
      const schedulePayload = card.querySelector(SELECTORS.schedule)?.textContent || '';
      const schedule = this.hoursApi?.parseSchedule(schedulePayload) || null;

      return {
        id: card.dataset.locationId,
        name: card.dataset.locationName || '',
        type: card.dataset.locationType || 'store',
        typeLabel: card.dataset.locationTypeLabel || '',
        searchText: StoreLocator.normalizeText(card.dataset.locationSearch),
        address: card.dataset.locationAddress || '',
        city: card.dataset.locationCity || '',
        postcode: card.dataset.locationPostcode || '',
        hours: card.dataset.locationHours || '',
        schedule,
        directionsUrl: card.dataset.locationDirectionsUrl || '',
        bookingUrl: card.dataset.locationBookingUrl || '',
        latitude,
        longitude,
        hasCoordinates: latitude !== null && longitude !== null,
        card,
      };
    }

    enhanceLocationCards() {
      this.locations.forEach((location) => {
        const slot = location.card.querySelector('[data-location-hours-widget-slot]');
        const fallback = location.card.querySelector('[data-location-hours-fallback]');
        const widget = this.createOpeningHoursWidget(location, {
          allowFallback: false,
          surface: 'card',
        });

        if (slot) slot.replaceChildren(...(widget ? [widget] : []));
        if (fallback) fallback.hidden = Boolean(widget);
      });
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.elements.searchForm?.addEventListener(
        'submit',
        (event) => this.handleSearchSubmit(event),
        { signal }
      );

      this.elements.search.addEventListener(
        'input',
        (event) => {
          this.cancelPlaceLookup();
          this.clearGeolocationStatus();
          this.resetRoutePreview();
          this.state.query = StoreLocator.normalizeText(event.target.value);
          this.state.searchMode = 'local';
          this.update();
        },
        { signal }
      );

      this.elements.filters.forEach((filter) => {
        filter.addEventListener(
          'click',
          () => {
            this.cancelPlaceLookup();
            this.clearGeolocationStatus();
            const hasRouteOrigin = Boolean(this.state.userLocation);
            this.resetRoutePreview({ keepOrigin: hasRouteOrigin });
            this.state.typeFilter = filter.dataset.locatorFilter || 'all';
            this.updateFilterButtons();
            this.update();
            if (hasRouteOrigin) this.selectNearestForOrigin({ scrollResult: true });
          },
          { signal }
        );
      });

      this.elements.reset?.addEventListener(
        'click',
        () => {
          this.cancelPlaceLookup();
          this.clearGeolocationStatus();
          this.resetRoutePreview();
          this.resetFilters();
          this.elements.search.focus();
        },
        { signal }
      );

      this.elements.geolocate?.addEventListener(
        'click',
        () => this.findNearestLocation(),
        { signal }
      );

      this.elements.routeModes.forEach((modeButton) => {
        modeButton.addEventListener(
          'click',
          () => {
            const routeMode = this.routeApi?.normalizeMode(
              modeButton.dataset.locatorRouteMode
            );
            if (!routeMode || routeMode === this.state.routeMode) return;

            this.state.routeMode = routeMode;
            this.updateRouteModeButtons();

            const destination = this.locationsById.get(this.state.routeDestinationId);
            if (destination) this.showRoutePreview(destination);
          },
          { signal }
        );
      });

      window.addEventListener('resize', () => this.scheduleCardTitleAlignment(), { signal });

      this.elements.results.addEventListener(
        'click',
        (event) => {
          const selectButton = event.target.closest(SELECTORS.select);
          if (selectButton && this.elements.results.contains(selectButton)) {
            this.selectLocation(selectButton.dataset.locationSelect);
            return;
          }

          const resultCard = event.target.closest('[data-location-result-card]');
          const interactiveElement = event.target.closest('a, button, details');
          if (
            !resultCard ||
            interactiveElement ||
            !this.elements.results.contains(resultCard)
          ) {
            return;
          }

          this.selectLocation(resultCard.dataset.locationResultCard);
        },
        { signal }
      );
    }

    update() {
      const effectiveQuery = this.state.searchMode === 'place' ? '' : this.state.query;
      const localMatches = this.locations.filter((location) =>
        StoreLocator.matches(location, effectiveQuery, this.state.typeFilter)
      );
      const hasPendingPlaceSearch =
        this.state.searchMode === 'local' &&
        effectiveQuery !== '' &&
        localMatches.length === 0;
      let visibleLocations = hasPendingPlaceSearch
        ? this.locations.filter((location) =>
            StoreLocator.matches(location, '', this.state.typeFilter)
          )
        : localMatches;

      if (this.state.userLocation && this.distanceApi?.sortLocationsByDistance) {
        visibleLocations = this.distanceApi.sortLocationsByDistance(
          visibleLocations,
          this.state.userLocation
        );
      }

      this.state.visibleLocationIds = new Set(visibleLocations.map((location) => location.id));

      if (
        this.state.selectedLocationId &&
        !this.state.visibleLocationIds.has(this.state.selectedLocationId)
      ) {
        this.state.selectedLocationId = null;
      }

      this.locations.forEach((location) => {
        location.card.hidden = !this.state.visibleLocationIds.has(location.id);
      });

      this.renderResults(visibleLocations);
      this.updateStatus(visibleLocations.length);
      this.updateEmptyState(visibleLocations.length);
      this.updateSearchGuidance(hasPendingPlaceSearch);
      this.syncSelection();
      this.updateMap(visibleLocations);
      this.scheduleCardTitleAlignment();

      this.dispatchEvent(
        new CustomEvent('store-locator:filtered', {
          bubbles: true,
          detail: {
            visibleLocationIds: [...this.state.visibleLocationIds],
            selectedLocationId: this.state.selectedLocationId,
          },
        })
      );
    }

    scheduleCardTitleAlignment() {
      window.cancelAnimationFrame(this.cardTitleAlignmentFrame);
      this.cardTitleAlignmentFrame = window.requestAnimationFrame(() =>
        this.alignLocationCardTitles()
      );
    }

    alignLocationCardTitles() {
      const titleEntries = this.locations
        .map((location) => ({
          card: location.card,
          title: location.card.querySelector(SELECTORS.cardTitle),
        }))
        .filter(({ title }) => title);

      titleEntries.forEach(({ title }) => {
        title.style.minHeight = '';
      });

      if (!window.matchMedia('(min-width: 990px)').matches) return;

      const rows = new Map();
      titleEntries
        .filter(({ card }) => !card.hidden)
        .forEach((entry) => {
          const rowTop = Math.round(entry.card.getBoundingClientRect().top);
          const row = rows.get(rowTop) || [];
          row.push(entry);
          rows.set(rowTop, row);
        });

      rows.forEach((row) => {
        const tallestTitle = Math.max(
          ...row.map(({ title }) => title.getBoundingClientRect().height)
        );

        row.forEach(({ title }) => {
          title.style.minHeight = `${Math.ceil(tallestTitle)}px`;
        });
      });
    }

    updateFilterButtons() {
      this.elements.filters.forEach((filter) => {
        filter.setAttribute(
          'aria-pressed',
          String(filter.dataset.locatorFilter === this.state.typeFilter)
        );
      });
    }

    updateStatus(count) {
      if (!this.elements.status) return;

      this.elements.status.textContent = `${count} ${count === 1 ? 'location' : 'locations'}`;
    }

    updateEmptyState(count) {
      if (this.elements.empty) this.elements.empty.hidden = count !== 0;
      this.elements.results.hidden = count === 0;
    }

    updateSearchGuidance(hasPendingPlaceSearch) {
      const status = this.elements.geolocationStatus;
      if (!status) return;

      if (hasPendingPlaceSearch) {
        this.showGeolocationStatus(
          'Select Search or press Enter to find your nearest location.',
          'prompt'
        );
        return;
      }

      if (status.dataset.state !== 'prompt') return;
      status.textContent = '';
      delete status.dataset.state;
      status.hidden = true;
    }

    resetFilters() {
      this.state.query = '';
      this.state.searchMode = 'local';
      this.state.typeFilter = 'all';
      this.state.selectedLocationId = null;
      this.state.nearestLocationId = null;
      this.elements.search.value = '';
      this.updateFilterButtons();
      this.update();
    }

    async handleSearchSubmit(event) {
      event.preventDefault();

      const submittedQuery = this.elements.search.value.trim();
      const normalizedQuery = StoreLocator.normalizeText(submittedQuery);
      const queryType = this.geocoderApi?.classifyQuery(submittedQuery);

      if (!queryType || queryType === 'invalid') {
        this.showGeolocationStatus('Enter a complete UK postcode or address.', 'error');
        this.elements.search.focus();
        return;
      }

      const localMatches = this.locations.filter((location) =>
        StoreLocator.matches(location, normalizedQuery, this.state.typeFilter)
      );

      if (localMatches.length > 0) {
        this.cancelPlaceLookup();
        this.clearGeolocationStatus();
        this.resetRoutePreview();
        this.state.query = normalizedQuery;
        this.state.searchMode = 'local';
        this.update();
        return;
      }

      await this.findNearestFromSearch(submittedQuery);
    }

    async findNearestFromSearch(query) {
      if (!this.geocoderApi || !this.distanceApi) {
        this.showGeolocationStatus(
          'Address search is temporarily unavailable. Try Use my location instead.',
          'error'
        );
        return;
      }

      const candidates = this.getMappedCandidates();
      if (candidates.length === 0) {
        this.showGeolocationStatus(
          'No mapped locations match this filter. Choose another filter and try again.',
          'error'
        );
        return;
      }

      this.cancelPlaceLookup();
      this.resetRoutePreview();
      const requestId = (this.placeRequestId || 0) + 1;
      this.placeRequestId = requestId;
      this.placeAbortController = new AbortController();
      this.setSearchLoading(true);
      this.showGeolocationStatus('Finding the nearest Islander location...', 'loading');

      try {
        const place = await this.resolveSearchPlace(query, requestId);
        if (!this.initialized || requestId !== this.placeRequestId) return;

        this.setSearchLoading(false);
        if (!place) {
          this.restoreLocationsAfterSearchFailure();
          this.showGeolocationStatus(
            'We could not find that place. Check the postcode or address and try again.',
            'error'
          );
          return;
        }

        const nearest = this.distanceApi.findNearestLocation(candidates, place);
        if (!nearest) {
          this.restoreLocationsAfterSearchFailure();
          this.showGeolocationStatus(
            'No mapped locations match this filter. Choose another filter and try again.',
            'error'
          );
          return;
        }

        this.state.query = '';
        this.state.searchMode = 'place';
        this.state.userLocation = {
          latitude: place.latitude,
          longitude: place.longitude,
          source: 'search',
          label: place.label || query,
        };
        this.state.nearestLocationId = nearest.location.id;
        this.update();
        this.showNearestDistance(nearest.location, nearest.distanceMiles);
        this.selectLocation(nearest.location.id, { scrollResult: true });
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.placeRequestId) return;

        console.warn('The store location search could not be completed.', error);
        this.setSearchLoading(false);
        this.restoreLocationsAfterSearchFailure();
        this.showGeolocationStatus(
          'Address search is temporarily unavailable. Try again or use your current location.',
          'error'
        );
      }
    }

    async resolveSearchPlace(query, requestId) {
      const cacheKey = this.geocoderApi.normalizeCacheKey(query);
      if (this.placeCache.has(cacheKey)) return this.placeCache.get(cacheKey);

      const queryType = this.geocoderApi.classifyQuery(query);
      let place = null;

      if (queryType === 'postcode') {
        place = await this.requestPlace(
          this.geocoderApi.buildPostcodeUrl(query),
          this.geocoderApi.parsePostcodeResponse,
          requestId
        );
      }

      if (!place) {
        place = await this.requestPlace(
          this.geocoderApi.buildAddressUrl(query),
          this.geocoderApi.parseAddressResponse,
          requestId,
          true
        );
      }

      if (requestId === this.placeRequestId) this.placeCache.set(cacheKey, place);
      return place;
    }

    async requestPlace(url, parser, requestId, throttle = false) {
      if (!url || typeof parser !== 'function') return null;

      if (throttle) {
        const requestDelay = Math.max(
          0,
          1_000 - (Date.now() - (this.lastAddressRequestAt || 0))
        );
        if (requestDelay > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, requestDelay));
        }
        if (requestId !== this.placeRequestId) {
          throw new DOMException('Search superseded', 'AbortError');
        }
        this.lastAddressRequestAt = Date.now();
      }

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.placeAbortController.signal,
      });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`Place lookup failed with ${response.status}`);

      return parser(await response.json());
    }

    getMappedCandidates() {
      return this.locations.filter(
        (location) =>
          location.hasCoordinates && StoreLocator.matches(location, '', this.state.typeFilter)
      );
    }

    selectNearestForOrigin(options = {}) {
      if (!this.state.userLocation || !this.distanceApi) return;

      const nearest = this.distanceApi.findNearestLocation(
        this.getMappedCandidates(),
        this.state.userLocation
      );
      if (!nearest) {
        this.showGeolocationStatus(
          'No mapped locations match this filter. Choose another filter and try again.',
          'error'
        );
        return;
      }

      this.state.nearestLocationId = nearest.location.id;
      this.showNearestDistance(nearest.location, nearest.distanceMiles);
      this.selectLocation(nearest.location.id, options);
    }

    showNearestDistance(location, distance) {
      const formattedDistance = this.distanceApi?.formatMiles(distance);
      this.showGeolocationStatus(
        `Nearest location: ${location.name}${
          formattedDistance ? ` - approximately ${formattedDistance} away` : ''
        }. Loading the ${this.state.routeMode} route...`,
        'success'
      );
    }

    restoreLocationsAfterSearchFailure() {
      this.state.query = '';
      this.state.searchMode = 'local';
      this.state.selectedLocationId = null;
      this.state.nearestLocationId = null;
      this.state.userLocation = null;
      this.update();
    }

    setSearchLoading(loading) {
      if (!this.elements.searchSubmit) return;

      this.elements.searchSubmit.disabled = loading;
      this.elements.searchSubmit.setAttribute('aria-busy', String(loading));
    }

    cancelPlaceLookup() {
      this.placeRequestId = (this.placeRequestId || 0) + 1;
      this.placeAbortController?.abort();
      this.setSearchLoading(false);
    }

    findNearestLocation() {
      this.cancelPlaceLookup();
      this.clearGeolocationStatus();
      this.resetRoutePreview();

      if (!this.distanceApi || !navigator.geolocation) {
        this.showGeolocationStatus(
          'Your browser cannot provide a location. Enter a city or postcode instead.',
          'error'
        );
        return;
      }

      const candidates = this.locations.filter(
        (location) =>
          location.hasCoordinates && StoreLocator.matches(location, '', this.state.typeFilter)
      );

      if (candidates.length === 0) {
        this.showGeolocationStatus(
          'No mapped locations match the selected filter. Try another filter or enter a postcode.',
          'error'
        );
        return;
      }

      const requestId = (this.geolocationRequestId || 0) + 1;
      this.geolocationRequestId = requestId;
      this.setGeolocationLoading(true);
      this.showGeolocationStatus('Finding your nearest location…', 'loading');

      try {
        navigator.geolocation.getCurrentPosition(
          (position) => this.handleGeolocationSuccess(position, candidates, requestId),
          (error) => this.handleGeolocationError(error, requestId),
          {
            enableHighAccuracy: false,
            timeout: 10_000,
            maximumAge: 300_000,
          }
        );
      } catch (_error) {
        this.handleGeolocationError(null, requestId);
      }
    }

    handleGeolocationSuccess(position, candidates, requestId) {
      if (!this.initialized || requestId !== this.geolocationRequestId) return;

      this.setGeolocationLoading(false);

      const nearest = this.distanceApi?.findNearestLocation(candidates, {
        latitude: position?.coords?.latitude,
        longitude: position?.coords?.longitude,
      });

      if (!nearest) {
        this.showGeolocationStatus(
          'Your nearest location could not be calculated. Enter a city or postcode instead.',
          'error'
        );
        return;
      }

      this.state.query = '';
      this.state.searchMode = 'local';
      this.elements.search.value = '';
      this.state.userLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        source: 'device',
        label: 'Your location',
      };
      this.state.nearestLocationId = nearest.location.id;
      this.update();

      const formattedDistance = this.distanceApi.formatMiles(nearest.distanceMiles);
      this.showGeolocationStatus(
        `Nearest location: ${nearest.location.name}${
          formattedDistance ? ` — approximately ${formattedDistance} away` : ''
        }. Select another location to compare distance and routes.`,
        'success'
      );
      this.showNearestDistance(nearest.location, nearest.distanceMiles);
      this.selectLocation(nearest.location.id, { scrollResult: true });
    }

    updateSelectedLocationDistance(location) {
      if (!this.state.userLocation || !location || !this.distanceApi) return;

      const distanceMiles = this.distanceApi.distanceMiles(this.state.userLocation, location);
      const formattedDistance = this.distanceApi.formatMiles(distanceMiles);
      this.showGeolocationStatus(
        formattedDistance
          ? `Distance to ${location.name}: approximately ${formattedDistance}.`
          : `Showing the route to ${location.name}.`,
        'success'
      );
    }

    handleGeolocationError(error, requestId) {
      if (!this.initialized || requestId !== this.geolocationRequestId) return;

      this.setGeolocationLoading(false);

      let message = 'Your location could not be found. Enter a city or postcode instead.';
      if (error?.code === 1) {
        message = 'Location access was not allowed. Enter a city or postcode instead.';
      } else if (error?.code === 3) {
        message = 'Finding your location took too long. Try again or enter a postcode.';
      }

      this.showGeolocationStatus(message, 'error');
    }

    setGeolocationLoading(loading) {
      if (!this.elements.geolocate) return;

      this.elements.geolocate.disabled = loading;
      this.elements.geolocate.setAttribute('aria-busy', String(loading));
      if (this.elements.geolocateLabel) {
        this.elements.geolocateLabel.textContent = loading
          ? 'Finding your location…'
          : 'Use my location';
      }
    }

    showGeolocationStatus(message, state) {
      if (!this.elements.geolocationStatus) return;

      this.elements.geolocationStatus.textContent = message;
      this.elements.geolocationStatus.dataset.state = state;
      this.elements.geolocationStatus.hidden = false;
    }

    clearGeolocationStatus() {
      this.geolocationRequestId = (this.geolocationRequestId || 0) + 1;
      this.setGeolocationLoading(false);
      if (!this.elements.geolocationStatus) return;

      this.elements.geolocationStatus.textContent = '';
      delete this.elements.geolocationStatus.dataset.state;
      this.elements.geolocationStatus.hidden = true;
    }

    renderResults(locations) {
      const fragment = document.createDocumentFragment();

      locations.forEach((location) => {
        fragment.append(this.createResult(location));
      });

      this.elements.results.replaceChildren(fragment);
    }

    createResult(location) {
      const item = document.createElement('li');
      const result = document.createElement('article');
      const type = document.createElement('p');
      const heading = document.createElement('h3');
      const selectButton = document.createElement('button');

      item.className = 'store-locator__result-item';
      item.dataset.locationResult = location.id;

      result.className = 'store-locator__result';
      result.dataset.locationResultCard = location.id;

      type.className = 'store-locator__result-type';
      type.textContent = location.typeLabel;

      heading.className = 'store-locator__result-heading';
      selectButton.className = 'store-locator__result-select';
      selectButton.type = 'button';
      selectButton.dataset.locationSelect = location.id;
      selectButton.textContent = location.name;
      selectButton.setAttribute('aria-label', `Select ${location.name}`);
      heading.append(selectButton);

      result.append(type, heading);

      const openingHours = this.createOpeningHoursWidget(location, { surface: 'result' });
      if (openingHours) result.append(openingHours);

      const address = this.formatAddress(location);
      if (address) {
        const addressElement = document.createElement('p');
        addressElement.className = 'store-locator__result-address';
        addressElement.textContent = address;
        result.append(addressElement);
      }

      const actions = this.createResultActions(location);
      if (actions) result.append(actions);

      item.append(result);
      return item;
    }

    createResultActions(location) {
      if (!location.bookingUrl && !location.directionsUrl) return null;

      const actions = document.createElement('div');
      actions.className = 'store-locator__result-actions';

      if (location.bookingUrl) {
        actions.append(
          this.createActionLink(
            location.bookingUrl,
            this.dataset.bookLabel || 'Book Now',
            location.name
          )
        );
      }

      if (location.directionsUrl) {
        actions.append(
          this.createActionLink(
            location.directionsUrl,
            this.dataset.directionsLabel || 'Get Directions',
            location.name
          )
        );
      }

      return actions;
    }

    createActionLink(url, label, locationName) {
      const link = document.createElement('a');
      const accessibleSuffix = document.createElement('span');

      link.className = 'store-locator__result-action';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.append(document.createTextNode(label));

      accessibleSuffix.className = 'visually-hidden';
      accessibleSuffix.textContent = ` for ${locationName} (opens in a new tab)`;
      link.append(accessibleSuffix);

      return link;
    }

    formatAddress(location) {
      return [location.address, [location.city, location.postcode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
    }

    createHoursFallback(location, surface) {
      const fallback = document.createElement('p');
      const className =
        surface === 'popup'
          ? 'store-locator__popup-hours'
          : surface === 'card'
            ? 'store-location-card__hours'
            : 'store-locator__result-hours';

      fallback.className = className;
      fallback.textContent = location.hours || 'Hours unavailable';

      if (!location.hours) {
        fallback.classList.add('store-opening-hours__unavailable');
      }

      return fallback;
    }

    createOpeningHoursWidget(location, options = {}) {
      const { allowFallback = true, surface = 'result' } = options;
      const status = location.schedule
        ? this.hoursApi?.getStatus(location.schedule, new Date())
        : null;

      if (!location.schedule || !status || status.state === 'unavailable') {
        return allowFallback ? this.createHoursFallback(location, surface) : null;
      }

      const details = document.createElement('details');
      const summary = document.createElement('summary');
      const statusGroup = document.createElement('span');
      const statusDot = document.createElement('span');
      const statusText = document.createElement('span');
      const transition = document.createElement('span');
      const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const arrowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const scheduleList = document.createElement('div');

      details.className = `store-opening-hours store-opening-hours--${surface}`;
      details.dataset.openingHoursWidget = '';
      details.dataset.locationId = location.id;

      summary.className = 'store-opening-hours__summary';
      statusGroup.className = 'store-opening-hours__status';
      statusDot.className = 'store-opening-hours__status-dot';
      statusDot.setAttribute('aria-hidden', 'true');
      statusText.className = 'store-opening-hours__status-text';
      statusText.dataset.openingHoursStatus = '';
      transition.className = 'store-opening-hours__transition';
      transition.dataset.openingHoursTransition = '';

      arrow.classList.add('store-opening-hours__arrow');
      arrow.setAttribute('viewBox', '0 0 16 10');
      arrow.setAttribute('width', '16');
      arrow.setAttribute('height', '10');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.setAttribute('focusable', 'false');
      arrowPath.setAttribute('d', 'M1.5 1.5 8 8l6.5-6.5');
      arrowPath.setAttribute('fill', 'none');
      arrowPath.setAttribute('stroke', 'currentColor');
      arrowPath.setAttribute('stroke-linecap', 'round');
      arrowPath.setAttribute('stroke-linejoin', 'round');
      arrowPath.setAttribute('stroke-width', '1.75');
      arrow.append(arrowPath);

      statusGroup.append(statusDot, statusText);
      summary.append(statusGroup, transition, arrow);

      scheduleList.className = 'store-opening-hours__schedule';
      scheduleList.setAttribute('role', 'list');

      location.schedule.days.forEach((day, index) => {
        const row = document.createElement('div');
        const dayName = document.createElement('span');
        const todayLabel = document.createElement('span');
        const hours = document.createElement('span');

        row.className = 'store-opening-hours__day';
        row.dataset.openingHoursDay = String(index);
        row.setAttribute('role', 'listitem');
        dayName.className = 'store-opening-hours__day-name';
        dayName.append(document.createTextNode(day.label));
        todayLabel.className = 'visually-hidden';
        todayLabel.dataset.openingHoursTodayLabel = '';
        todayLabel.textContent = ' (Today)';
        dayName.append(todayLabel);
        hours.className = 'store-opening-hours__day-time';
        hours.textContent = day.closed
          ? 'Closed'
          : `${this.hoursApi.formatTime(day.openMinutes)} – ${this.hoursApi.formatTime(
              day.closeMinutes
            )}`;

        row.append(dayName, hours);
        scheduleList.append(row);
      });

      details.append(summary, scheduleList);
      this.updateOpeningHoursWidget(details, location, status);
      return details;
    }

    updateOpeningHoursWidget(widget, location, suppliedStatus) {
      if (!widget || !location?.schedule || !this.hoursApi) return;

      const status = suppliedStatus || this.hoursApi.getStatus(location.schedule, new Date());
      if (!status || status.state === 'unavailable') return;

      widget.classList.toggle('store-opening-hours--open', status.state === 'open');
      widget.classList.toggle('store-opening-hours--closed', status.state === 'closed');

      const statusText = widget.querySelector('[data-opening-hours-status]');
      const transition = widget.querySelector('[data-opening-hours-transition]');
      if (statusText) statusText.textContent = status.label;
      if (transition) {
        transition.textContent = status.transition ? `· ${status.transition}` : '';
        transition.hidden = !status.transition;
      }

      widget.querySelectorAll('[data-opening-hours-day]').forEach((row) => {
        const isToday = Number.parseInt(row.dataset.openingHoursDay, 10) === status.currentDayIndex;
        row.classList.toggle('store-opening-hours__day--today', isToday);
        if (isToday) {
          row.setAttribute('aria-current', 'date');
        } else {
          row.removeAttribute('aria-current');
        }

        const todayLabel = row.querySelector('[data-opening-hours-today-label]');
        if (todayLabel) todayLabel.hidden = !isToday;
      });
    }

    refreshOpeningHours() {
      if (!this.hoursApi) return;

      const widgets = new Set(this.querySelectorAll(SELECTORS.hoursWidget));

      this.markers?.forEach((marker) => {
        const content = marker.getPopup()?.getContent();
        if (!(content instanceof Element)) return;

        if (content.matches(SELECTORS.hoursWidget)) widgets.add(content);
        content.querySelectorAll(SELECTORS.hoursWidget).forEach((widget) => widgets.add(widget));
      });

      widgets.forEach((widget) => {
        const location = this.locationsById.get(widget.dataset.locationId);
        if (location) this.updateOpeningHoursWidget(widget, location);
      });
    }

    startOpeningHoursRefresh() {
      if (!this.hoursApi) return;

      window.clearInterval(this.hoursRefreshTimer);
      this.hoursRefreshTimer = window.setInterval(() => this.refreshOpeningHours(), 60_000);

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'visible') this.refreshOpeningHours();
        },
        { signal: this.abortController.signal }
      );
    }

    initMap() {
      const mappedLocations = this.locations.filter((location) => location.hasCoordinates);

      if (
        !this.elements.map ||
        !this.elements.mapFallback ||
        mappedLocations.length === 0 ||
        typeof window.L === 'undefined' ||
        typeof window.L.markerClusterGroup !== 'function'
      ) {
        this.showMapFallback();
        return;
      }

      try {
        const defaultLatitude =
          StoreLocator.parseCoordinate(this.dataset.defaultLatitude, -90, 90) ??
          mappedLocations[0].latitude;
        const defaultLongitude =
          StoreLocator.parseCoordinate(this.dataset.defaultLongitude, -180, 180) ??
          mappedLocations[0].longitude;

        this.elements.map.hidden = false;
        this.elements.mapFallback.hidden = true;
        this.map = window.L.map(this.elements.map, {
          center: [defaultLatitude, defaultLongitude],
          zoom: 6,
          maxZoom: 19,
          scrollWheelZoom: true,
          zoomControl: true,
        });

        const streetLayer = window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        });
        streetLayer.once('tileerror', () => this.showMapFallback());

        const satelliteImagery = window.L.tileLayer(
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          {
            attribution:
              'Tiles &copy; Esri &mdash; Sources: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
            maxZoom: 19,
          }
        );
        this.map.createPane('storeLocatorLabels');
        this.map.getPane('storeLocatorLabels').style.zIndex = '250';
        this.map.getPane('storeLocatorLabels').style.pointerEvents = 'none';
        const satelliteLabels = window.L.tileLayer(
          'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
          {
            maxZoom: 19,
            pane: 'storeLocatorLabels',
          }
        );

        this.baseLayers = {
          monochrome: streetLayer,
          street: streetLayer,
          satellite: window.L.layerGroup([satelliteImagery, satelliteLabels]),
        };
        this.setMapView(this.state.mapView);

        this.markerCluster = window.L.markerClusterGroup({
          maxClusterRadius: 42,
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: true,
        });
        this.map.addLayer(this.markerCluster);

        this.markers = new Map();
        mappedLocations.forEach((location) => {
          const marker = this.createMarker(location);
          this.markers.set(location.id, marker);
        });

        if (this.elements.mapLegend) this.elements.mapLegend.hidden = false;
        this.map.attributionControl?.addAttribution(
          'Routing: <a href="https://routing.openstreetmap.de/about.html" target="_blank" rel="noopener noreferrer">openstreetmap.de</a> · <a href="https://www.openstreetmap.org/fixthemap" target="_blank" rel="noopener noreferrer">Fix the map</a>'
        );
        this.map.attributionControl?.addAttribution(
          'Search: <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>'
        );
        this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
        this.resizeObserver.observe(this.elements.map);
        requestAnimationFrame(() => this.map?.invalidateSize());
      } catch (error) {
        console.warn('The store map could not be initialized.', error);
        this.showMapFallback();
      }
    }

    setMapView(viewName) {
      const normalizedView = ['monochrome', 'street', 'satellite'].includes(viewName)
        ? viewName
        : 'monochrome';
      const nextLayer = this.baseLayers?.[normalizedView];
      if (!this.map || !nextLayer) return;

      Object.values(this.baseLayers).forEach((layer) => {
        if (layer !== nextLayer && this.map.hasLayer(layer)) this.map.removeLayer(layer);
      });

      if (!this.map.hasLayer(nextLayer)) nextLayer.addTo(this.map);
      this.state.mapView = normalizedView;
      this.elements.map.dataset.mapView = normalizedView;
    }

    createMarker(location) {
      const marker = window.L.marker([location.latitude, location.longitude], {
        icon: this.createMarkerIcon(location, false),
        keyboard: true,
        riseOnHover: true,
        title: location.name,
      });

      marker.bindPopup(this.createPopup(location), {
        closeButton: true,
        maxWidth: 250,
        minWidth: 190,
      });

      marker.on('click', () => {
        this.selectLocation(location.id, {
          keepPopup: true,
          revealMarker: false,
          scrollResult: true,
        });
        if (!this.state.userLocation) this.centerSelectedMarker(marker);
      });

      marker.on('mouseover', () => marker.openPopup());
      marker.on('mouseout', () => {
        if (this.state.selectedLocationId !== location.id) marker.closePopup();
      });

      return marker;
    }

    createMarkerIcon(location, selected) {
      const locationType = ['store', 'workshop', 'store-workshop'].includes(location.type)
        ? location.type
        : 'store';

      return window.L.divIcon({
        className: `store-locator__marker store-locator__marker--${locationType}${
          selected ? ' store-locator__marker--selected' : ''
        }`,
        html: '<span class="store-locator__marker-pin" aria-hidden="true"></span>',
        iconAnchor: selected ? [21, 42] : [18, 36],
        iconSize: selected ? [42, 42] : [36, 36],
        popupAnchor: [0, selected ? -38 : -32],
      });
    }

    createPopup(location) {
      const popup = document.createElement('article');
      const heading = document.createElement('h3');
      const address = this.formatAddress(location);

      popup.className = 'store-locator__popup';
      heading.className = 'store-locator__popup-heading';
      heading.textContent = location.name;
      popup.append(heading);

      const openingHours = this.createOpeningHoursWidget(location, { surface: 'popup' });
      if (openingHours) popup.append(openingHours);

      if (address) {
        const addressElement = document.createElement('p');
        addressElement.className = 'store-locator__popup-address';
        addressElement.textContent = address;
        popup.append(addressElement);
      }

      const actions = this.createResultActions(location);
      if (actions) {
        actions.classList.add('store-locator__popup-actions');
        popup.append(actions);
      }

      return popup;
    }

    updateMap(visibleLocations) {
      if (!this.map || !this.markerCluster || !this.markers) return;

      const visibleMarkers = visibleLocations
        .map((location) => this.markers.get(location.id))
        .filter(Boolean);

      this.markerCluster.clearLayers();
      if (visibleMarkers.length === 0) return;

      this.markerCluster.addLayers(visibleMarkers);
      this.fitVisibleMarkers(visibleMarkers);
    }

    fitVisibleMarkers(markers) {
      if (!this.map || markers.length === 0) return;

      const singleLocationZoom = Number.parseInt(this.dataset.singleLocationZoom, 10) || 14;

      if (markers.length === 1) {
        this.map.setView(markers[0].getLatLng(), singleLocationZoom);
        return;
      }

      const panelWidth =
        window.matchMedia('(min-width: 990px)').matches && this.elements.mapLayout
          ? this.querySelector('.store-locator__panel')?.getBoundingClientRect().width || 0
          : 0;
      const horizontalPadding = 40;

      this.map.fitBounds(window.L.featureGroup(markers).getBounds(), {
        animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        maxZoom: singleLocationZoom,
        paddingTopLeft: [panelWidth + horizontalPadding, 40],
        paddingBottomRight: [horizontalPadding, 40],
      });
    }

    revealMarker(locationId) {
      const marker = this.markers?.get(locationId);
      if (!marker || !this.markerCluster?.hasLayer(marker)) return;

      this.markerCluster.zoomToShowLayer(marker, () => {
        this.centerSelectedMarker(marker);
      });
    }

    centerSelectedMarker(marker) {
      if (!this.map || !marker || !this.elements.map) return;

      marker.openPopup();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.map || !this.markerCluster?.hasLayer(marker)) return;

          const mapRect = this.elements.map.getBoundingClientRect();
          const panelRect = this.querySelector('.store-locator__panel')?.getBoundingClientRect();
          const panelOverlapsMap =
            panelRect &&
            panelRect.bottom > mapRect.top &&
            panelRect.top < mapRect.bottom &&
            panelRect.right > mapRect.left &&
            panelRect.left < mapRect.right;
          const usableLeft = panelOverlapsMap
            ? Math.min(
                mapRect.width * 0.6,
                Math.max(0, panelRect.right - mapRect.left + 24)
              )
            : 0;
          const targetPoint = {
            x: usableLeft + (mapRect.width - usableLeft) / 2,
            y: mapRect.height * 0.58,
          };
          const markerPoint = this.map.latLngToContainerPoint(marker.getLatLng());
          const panOffset = [
            markerPoint.x - targetPoint.x,
            markerPoint.y - targetPoint.y,
          ];

          if (Math.hypot(...panOffset) < 2) return;

          const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches;

          this.map.stop();
          this.map.panBy(panOffset, {
            animate: !prefersReducedMotion,
            duration: 0.35,
          });
        });
      });
    }

    updateRouteModeButtons() {
      this.elements.routeModes.forEach((modeButton) => {
        modeButton.setAttribute(
          'aria-pressed',
          String(modeButton.dataset.locatorRouteMode === this.state.routeMode)
        );
      });
    }

    showRoutePreview(destination) {
      if (
        !this.map ||
        !this.routeApi ||
        !this.state.userLocation ||
        !destination?.hasCoordinates
      ) {
        return;
      }

      this.state.routeDestinationId = destination.id;
      if (this.elements.routeControls) this.elements.routeControls.hidden = false;
      this.updateRouteModeButtons();
      this.frameRouteDestination(destination);

      const routeUrl = this.routeApi.buildRouteUrl(
        this.state.userLocation,
        destination,
        this.state.routeMode
      );
      if (!routeUrl) {
        this.showRouteStatus(
          'The route preview is unavailable. Use Get Directions to continue in Google Maps.',
          'error'
        );
        return;
      }

      const requestId = (this.routeRequestId || 0) + 1;
      this.routeRequestId = requestId;
      this.routeAbortController?.abort();
      window.clearTimeout(this.routeRequestTimer);

      const cacheKey = routeUrl;
      const cachedRoute = this.routeCache.get(cacheKey);
      if (cachedRoute) {
        this.renderRoutePreview(cachedRoute, destination);
        return;
      }

      this.showRouteStatus(
        `Loading ${this.state.routeMode} route to ${destination.name}…`,
        'loading'
      );

      const requestDelay = Math.max(
        0,
        1_000 - (Date.now() - (this.lastRouteRequestAt || 0))
      );
      this.routeRequestTimer = window.setTimeout(
        () => this.requestRoutePreview(routeUrl, destination, cacheKey, requestId),
        requestDelay
      );
    }

    async requestRoutePreview(routeUrl, destination, cacheKey, requestId) {
      if (!this.initialized || requestId !== this.routeRequestId) return;

      this.lastRouteRequestAt = Date.now();
      this.routeAbortController = new AbortController();

      try {
        const response = await fetch(routeUrl, {
          headers: { Accept: 'application/json' },
          signal: this.routeAbortController.signal,
        });
        if (!response.ok) throw new Error(`Routing request failed with ${response.status}`);

        const route = this.routeApi.parseRoute(await response.json());
        if (!route) throw new Error('Routing response did not include a usable route');
        if (!this.initialized || requestId !== this.routeRequestId) return;

        this.routeCache.set(cacheKey, route);
        this.renderRoutePreview(route, destination);
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.routeRequestId) return;

        console.warn('The route preview could not be loaded.', error);
        this.removeRouteLayers();
        this.showRouteStatus(
          'The route preview is unavailable. Use Get Directions to continue in Google Maps.',
          'error'
        );
      }
    }

    renderRoutePreview(route, destination) {
      if (!this.map || typeof window.L === 'undefined') return;

      this.removeRouteLayers();

      const routeFeature = {
        type: 'Feature',
        properties: {},
        geometry: route.geometry,
      };
      const outline = window.L.geoJSON(routeFeature, {
        interactive: false,
        style: {
          color: '#ffffff',
          opacity: 0.92,
          weight: 10,
        },
      });
      const routeLine = window.L.geoJSON(routeFeature, {
        interactive: false,
        style: {
          color: '#236fc8',
          dashArray: this.state.routeMode === 'walking' ? '8 8' : null,
          lineCap: 'round',
          lineJoin: 'round',
          opacity: 0.96,
          weight: 6,
        },
      });

      this.routeLayer = window.L.featureGroup([outline, routeLine]).addTo(this.map);
      this.routeOriginMarker = window.L.circleMarker(
        [this.state.userLocation.latitude, this.state.userLocation.longitude],
        {
          className: 'store-locator__route-origin',
          color: '#ffffff',
          fillColor: '#236fc8',
          fillOpacity: 1,
          radius: 7,
          weight: 3,
        }
      )
        .bindTooltip(
          this.state.userLocation.source === 'search'
            ? 'Your searched location'
            : 'Your location'
        )
        .addTo(this.map);

      const routeBounds = this.routeLayer
        .getBounds()
        .extend([this.state.userLocation.latitude, this.state.userLocation.longitude])
        .extend([destination.latitude, destination.longitude]);
      this.fitRouteViewport(routeBounds);

      const formattedDistance = this.distanceApi?.formatMiles(
        Number.isFinite(route.distanceMeters) ? route.distanceMeters / 1609.344 : null
      );
      const formattedDuration = this.routeApi?.formatDuration(route.durationSeconds) || '';
      const journeyName = this.state.routeMode === 'walking' ? 'walk' : 'drive';
      const routeSummary = [`Showing ${this.state.routeMode} route to ${destination.name}`];
      if (formattedDistance) routeSummary.push(`about ${formattedDistance}`);
      if (formattedDuration) routeSummary.push(`${formattedDuration} estimated`);
      routeSummary.push('live traffic is not included');

      this.showRouteStatus(`${routeSummary.join(' · ')}.`, 'success');

      const distanceSummary = formattedDistance
        ? `approximately ${formattedDistance} away`
        : 'route available';
      const etaSummary = formattedDuration
        ? ` · Estimated ${journeyName}: ${formattedDuration}`
        : '';
      const statusLabel =
        destination.id === this.state.nearestLocationId
          ? `Nearest location: ${destination.name}`
          : `Distance to ${destination.name}`;

      this.showGeolocationStatus(
        `${statusLabel} - ${distanceSummary}${etaSummary}.`,
        'success'
      );
    }

    frameRouteDestination(destination) {
      if (
        !this.map ||
        typeof window.L === 'undefined' ||
        !this.state.userLocation ||
        !destination?.hasCoordinates
      ) {
        return;
      }

      this.removeRouteLayers();
      const destinationBounds = window.L.latLngBounds(
        [this.state.userLocation.latitude, this.state.userLocation.longitude],
        [destination.latitude, destination.longitude]
      );
      this.fitRouteViewport(destinationBounds);
    }

    fitRouteViewport(bounds) {
      if (!this.map || !bounds) return;

      const panelWidth = window.matchMedia('(min-width: 990px)').matches
        ? this.querySelector('.store-locator__panel')?.getBoundingClientRect().width || 0
        : 0;

      this.map.fitBounds(bounds, {
        animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        maxZoom: 13,
        paddingTopLeft: [panelWidth + 40, 50],
        paddingBottomRight: [40, 50],
      });
    }

    showRouteStatus(message, state) {
      if (!this.elements.routeStatus) return;

      this.elements.routeStatus.textContent = message;
      this.elements.routeStatus.dataset.state = state;
    }

    removeRouteLayers() {
      if (this.map && this.routeLayer) this.map.removeLayer(this.routeLayer);
      if (this.map && this.routeOriginMarker) this.map.removeLayer(this.routeOriginMarker);
      this.routeLayer = null;
      this.routeOriginMarker = null;
    }

    resetRoutePreview(options = {}) {
      this.routeRequestId = (this.routeRequestId || 0) + 1;
      this.routeAbortController?.abort();
      window.clearTimeout(this.routeRequestTimer);
      this.removeRouteLayers();
      if (!options.keepOrigin) {
        this.state.userLocation = null;
        this.state.nearestLocationId = null;
      }
      this.state.routeDestinationId = null;

      if (this.elements.routeControls) this.elements.routeControls.hidden = true;
      if (this.elements.routeStatus) {
        this.elements.routeStatus.textContent = '';
        delete this.elements.routeStatus.dataset.state;
      }
    }

    showMapFallback() {
      if (this.elements?.map) this.elements.map.hidden = true;
      if (this.elements?.mapLegend) this.elements.mapLegend.hidden = true;
      if (this.elements?.mapFallback) this.elements.mapFallback.hidden = false;
      this.destroyMap();
    }

    destroyMap() {
      this.markerCluster?.clearLayers();
      this.map?.remove();
      this.map = null;
      this.baseLayers = null;
      this.markerCluster = null;
      this.markers = null;
      this.routeLayer = null;
      this.routeOriginMarker = null;
    }

    selectLocation(locationId, options = {}) {
      if (!this.state.visibleLocationIds.has(locationId)) return;

      this.state.selectedLocationId = locationId;
      this.syncSelection();
      const selectedLocation = this.locationsById.get(locationId);
      const showsRoute = Boolean(this.state.userLocation && selectedLocation);

      if (options.scrollResult) {
        this.scrollResultIntoView(locationId);
      }

      if (!showsRoute && options.revealMarker !== false) {
        this.revealMarker(locationId);
      }

      if (showsRoute) {
        if (!options.keepPopup) this.map?.closePopup();
        this.updateSelectedLocationDistance(selectedLocation);
        this.showRoutePreview(selectedLocation);
      }

      this.dispatchEvent(
        new CustomEvent('store-locator:selected', {
          bubbles: true,
          detail: { locationId },
        })
      );
    }

    scrollResultIntoView(locationId) {
      const resultItem = [...this.elements.results.querySelectorAll(SELECTORS.result)].find(
        (result) => result.dataset.locationResult === locationId
      );
      const resultsShell = this.elements.results.closest('.store-locator__results-shell');

      if (
        !resultItem ||
        !resultsShell ||
        resultsShell.scrollHeight <= resultsShell.clientHeight
      ) {
        return;
      }

      const shellRect = resultsShell.getBoundingClientRect();
      const resultRect = resultItem.getBoundingClientRect();
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      resultsShell.scrollTo({
        top: Math.max(0, resultsShell.scrollTop + resultRect.top - shellRect.top),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    syncSelection() {
      this.locations.forEach((location) => {
        const selected = location.id === this.state.selectedLocationId;
        if (selected) {
          location.card.setAttribute('aria-current', 'location');
        } else {
          location.card.removeAttribute('aria-current');
        }
      });

      this.elements.results.querySelectorAll('[data-location-result-card]').forEach((result) => {
        const selected = result.dataset.locationResultCard === this.state.selectedLocationId;
        if (selected) {
          result.setAttribute('aria-current', 'true');
        } else {
          result.removeAttribute('aria-current');
        }
      });

      this.markers?.forEach((marker, locationId) => {
        const location = this.locationsById.get(locationId);
        if (!location) return;
        const selected = locationId === this.state.selectedLocationId;
        marker.setIcon(this.createMarkerIcon(location, selected));
        marker.setZIndexOffset(selected ? 1000 : 0);
      });
    }
  }

  if (!customElements.get('store-locator')) {
    customElements.define('store-locator', StoreLocator);
  }
})();
